import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../i18n/LanguageContext";
import apiClient from "../api/api-client";
import { handleApiError } from "../api/error-handler";
import {
  Company,
  CompanyCreateDTO,
  CompanyUpdateDTO,
  CompanyAddressCreateDTO,
  CompanyAcceptedTalent,
} from "../models/Company";
import { PaginatedResponse } from "../models/Pagination";
import { useState } from "react";
import { useAuthProvider } from "../auth/hooks/auth-provider";

/**
 * Hook to fetch all companies
 */
export const useCompanies = (page = 1, pageSize = 10, sortField?: string, sortDirection?: string) => {
  const { currentLanguage } = useLanguage();

  return useQuery<PaginatedResponse<Company>, Error>({
    queryKey: ["companies", page, pageSize, sortField, sortDirection, currentLanguage],
    queryFn: async () => {
      try {
        let url = `/companies?page=${page}&pageSize=${pageSize}`;
        if (sortField) url += `&sortField=${encodeURIComponent(sortField)}`;
        if (sortDirection) url += `&sortDirection=${encodeURIComponent(sortDirection)}`;

        const response = await apiClient.get<PaginatedResponse<Company>>(url);

        // The API now returns the paginated response directly in the body
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to fetch companies");
      }
    }
  });
};

/**
 * Hook to search companies by search term
 */
export const useSearchCompanies = () => {
  const [data, setData] = useState<PaginatedResponse<Company> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSearched, setIsSearched] = useState(false);

  const searchCompanies = async (
    searchTerm: string,
    page: number = 1,
    pageSize: number = 10,
    sectorId?: number,
    size?: string,
    type?: string,
    sortField?: string,
    sortDirection?: string
  ) => {
    if ((!searchTerm || searchTerm.trim().length < 2) && 
      !sectorId && !size && !type) {
    return;
  }

    setIsLoading(true);
    setError(null);
    setIsSearched(true);

    try {
      // Construct URL with query parameters
      let url = `/companies/search?searchTerm=${encodeURIComponent(searchTerm.trim())}&page=${page}&pageSize=${pageSize}`;
      
      // Add optional filters if they exist
      if (sectorId) url += `&sectorId=${sectorId}`;
      if (size) url += `&size=${encodeURIComponent(size)}`;
      if (type) url += `&type=${encodeURIComponent(type)}`;
      if (sortField) url += `&sortField=${encodeURIComponent(sortField)}`;
      if (sortDirection) url += `&sortDirection=${encodeURIComponent(sortDirection)}`;

      const response = await apiClient.get<PaginatedResponse<Company>>(url);
      
      // The API now returns the paginated response directly in the body
      setData(response.data);
    } catch (error) {
      setError(handleApiError(error, "Failed to search companies"));
    } finally {
      setIsLoading(false);
    }
  };

  const resetSearch = () => {
    setIsSearched(false);
    setData(null);
    setError(null);
  };

  return { data, isLoading, error, isSearched, searchCompanies, resetSearch };
};

/**
 * Hook to search companies by name
 */
export const useSearchCompaniesAutocomplete = (searchTerm: string, minChars: number = 2) => {
  const { currentLanguage } = useLanguage();

  return useQuery<Company[], Error>({
    queryKey: ["companies", "search", searchTerm, currentLanguage],
    queryFn: async () => {
      try {
        // Use maximum pageSize (100) to show all matching results with scroll
        // Search is already filtered by minimum 2 characters, so performance is not an issue
        const response = await apiClient.get<PaginatedResponse<Company>>(`/companies/search?searchTerm=${encodeURIComponent(searchTerm)}&pageSize=100`);
        return response.data.items ?? [];
      } catch (error) {
        throw handleApiError(error, "Failed to search companies");
      }
    },
    enabled: searchTerm.length >= minChars, // Only search when minimum characters are typed
    staleTime: 10000, // Cache results for 10 seconds
  });
};

/**
 * Hook to fetch a specific company by ID
 */
export const useCompany = (id: number) => {
  const { currentLanguage } = useLanguage();

  return useQuery<Company, Error>({
    queryKey: ["company", id, currentLanguage],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/companies/${id}`);
        return response.data;
      } catch (error) {
        throw handleApiError(error, `Failed to fetch company with ID ${id}`);
      }
    },
    enabled: id !== 0,
  });
};

/**
 * Hook to fetch accepted talents for a company
 */
export const useCompanyAcceptedTalents = (companyId: number) => {
  return useQuery<CompanyAcceptedTalent[], Error>({
    queryKey: ["company", companyId, "accepted-talents"],
    queryFn: async () => {
      try {
        const response = await apiClient.get<CompanyAcceptedTalent[]>(`/companies/${companyId}/accepted-talents`);
        return response.data;
      } catch (error) {
        throw handleApiError(error, `Failed to fetch accepted talents for company ${companyId}`);
      }
    },
    enabled: companyId !== 0,
  });
};

/**
 * Hook to create a new company
 */
export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (companyData: CompanyCreateDTO) => {
      try {
        const response = await apiClient.post("/companies", companyData);
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to create company");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    }
  });
};

/**
 * Hook to update a company
 */
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CompanyUpdateDTO }) => {
      try {
        const response = await apiClient.put(`/companies/${id}`, data);
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to update company");
      }
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["company", id] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    }
  });
};

/**
 * Hook to patch a company (for updating specific fields like translations)
 */
export const usePatchCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, operations, languageCode }: { id: number; operations: any[]; languageCode?: string }) => {
      try {
        const url = languageCode
          ? `/companies/${id}?languageCode=${languageCode}`
          : `/companies/${id}`;
        const response = await apiClient.patch(url, operations, {
          headers: {
            'Content-Type': 'application/json-patch+json'
          }
        });
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to update company");
      }
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["company", id] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["entityTranslations"] });
    }
  });
};

/**
 * Hook to delete a company
 */
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      try {
        const response = await apiClient.delete(`/companies/${id}`);
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to delete company");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    }
  });
};

/**
 * Hook to fetch address types
 */
export const useAddressTypes = () => {
  return useQuery<any[], Error>({
    queryKey: ["addressTypes"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/companies/addresses/types");
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to fetch address types");
      }
    }
  });
};

/**
 * Hook to add an address to a company
 */
export const useAddCompanyAddress = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (addressData: CompanyAddressCreateDTO) => {
      try {
        const response = await apiClient.post(`/companies/${addressData.companyId}/addresses`, addressData);
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to add company address");
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["company", variables.companyId] });
    }
  });
};

/**
 * Hook to patch a company address
 */
export const usePatchCompanyAddress = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      companyId, 
      addressId, 
      operations 
    }: { 
      companyId: number; 
      addressId: number;
      operations: any[] 
    }) => {
      try {
        const response = await apiClient.patch(
          `/companies/${companyId}/addresses/${addressId}`, 
          operations,
          {
            headers: {
              'Content-Type': 'application/json-patch+json'
            }
          }
        );
        return response.data;
      } catch (error) {
        throw handleApiError(error, "Failed to update company address");
      }
    },
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    }
  });
};

/**
 * Hook to fetch sector options
 */
export const useSectorOptions = () => {
  return useQuery<any[], Error>({
    queryKey: ["sectorOptions"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/companies/sectors");
        return response.data;
      } catch (error) {
        console.warn("Failed to fetch sector options", error);
        return null;
      }
    },
  });
};

/**
 * Hook to fetch company size options with fallback values
 */
export const useCompanySizeOptions = () => {
  return useQuery<string[], Error>({
    queryKey: ["companySizeOptions"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/companies/sizes");
        return response.data;
      } catch (error) {
        console.warn("Failed to fetch company size options, using fallback values", error);
        return null;
      }
    },
  });
};

/**
 * Hook to fetch company type options with fallback values
 */
export const useCompanyTypeOptions = () => {
  return useQuery<string[], Error>({
    queryKey: ["companyTypeOptions"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/companies/types");
        return response.data;
      } catch (error) {
        console.warn("Failed to fetch company type options, using fallback values", error);
        return null;
      }
    },
  });
};

/**
 * Hook to get the count of companies created by the current user
 * Used to enforce the "1 company per client user" rule
 */
export const useUserCreatedCompaniesCount = () => {
  const { user } = useAuthProvider();
  
  return useQuery<number, Error>({
    queryKey: ["userCreatedCompaniesCount", user?.id],
    queryFn: async () => {
      try {
        // Use the dedicated backend endpoint
        const response = await apiClient.get<number>("/users/me/companies/count");
        return response.data;
      } catch (error) {
        console.warn("Failed to fetch user created companies count", error);
        // Return 0 as fallback - user can still try to create (backend will handle the real validation)
        return 0;
      }
    },
    enabled: !!user?.id, // Only run when user is loaded
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};